/**
 * CookieSettingsDialog
 *
 * A modal dialog that lets users review and configure granular cookie/storage
 * consent preferences. Accessible and keyboard-navigable.
 *
 * Categories:
 * - Essential (locked on — cannot be disabled)
 * - Functional (optional, default off)
 * - Analytics  (optional, default off)
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@xanectech/ui/components';
import { Button, Switch, Label, Separator } from '@xanectech/ui/components';
import { ShieldCheck, Settings2, BarChart2, Lock } from 'lucide-react';
import { CONSENT_CATEGORIES } from '@xanectech/types';
import type { ConsentCategoryMeta } from '@xanectech/types';
import { useCookieConsent } from './CookieProvider';

const CATEGORY_ORDER: ConsentCategoryMeta['id'][] = [
  'essential',
  'functional',
  'analytics',
];

// Icon map for each category
const categoryIcons: Record<string, React.ReactNode> = {
  essential: (
    <ShieldCheck className='h-5 w-5 text-primary' aria-hidden='true' />
  ),
  functional: <Settings2 className='h-5 w-5 text-primary' aria-hidden='true' />,
  analytics: <BarChart2 className='h-5 w-5 text-primary' aria-hidden='true' />,
};

export const CookieSettingsDialog: React.FC = () => {
  const {
    appName,
    consentState,
    isSettingsOpen,
    closeSettings,
    savePreferences,
  } = useCookieConsent();

  // Local toggle state — initialised from current consent or defaults (off)
  const [functional, setFunctional] = useState<boolean>(
    consentState?.preferences.functional ?? false,
  );
  const [analytics, setAnalytics] = useState<boolean>(
    consentState?.preferences.analytics ?? false,
  );

  // Sync local state when the dialog opens (picks up changes from outside)
  useEffect(() => {
    if (isSettingsOpen) {
      setFunctional(consentState?.preferences.functional ?? false);
      setAnalytics(consentState?.preferences.analytics ?? false);
    }
  }, [isSettingsOpen, consentState]);

  const handleSave = () => {
    savePreferences({ functional, analytics });
  };

  const categories = React.useMemo(
    () =>
      CATEGORY_ORDER.flatMap((categoryId) => {
        const appSpecific = CONSENT_CATEGORIES.find(
          (category) => category.app === appName && category.id === categoryId,
        );

        if (appSpecific) {
          return [appSpecific];
        }

        const wildcard = CONSENT_CATEGORIES.find(
          (category) => category.app === '*' && category.id === categoryId,
        );

        return wildcard ? [wildcard] : [];
      }),
    [appName],
  );

  const getToggle = (meta: ConsentCategoryMeta) => {
    if (meta.required) {
      return (
        <div className='flex items-center gap-2'>
          <Lock className='h-4 w-4 text-muted-foreground' aria-hidden='true' />
          <span className='text-sm font-medium text-muted-foreground'>
            Always on
          </span>
        </div>
      );
    }

    const checked = meta.id === 'functional' ? functional : analytics;
    const onCheckedChange =
      meta.id === 'functional' ? setFunctional : setAnalytics;
    const switchId = `consent-${meta.id}`;

    return (
      <div className='flex items-center gap-2'>
        <Switch
          id={switchId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-label={`Toggle ${meta.label} cookies`}
        />
        <Label htmlFor={switchId} className='text-sm'>
          {checked ? 'On' : 'Off'}
        </Label>
      </div>
    );
  };

  return (
    <Dialog
      open={isSettingsOpen}
      onOpenChange={(open) => !open && closeSettings()}
    >
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Cookie Settings</DialogTitle>
          <DialogDescription>
            Manage how we store information in your browser. Essential storage
            is always active. All other categories are optional and off by
            default.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {categories.map((meta, index) => (
            <React.Fragment key={`${meta.app}-${meta.id}`}>
              {index > 0 && <Separator />}
              <div className='space-y-3'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='flex items-center gap-2'>
                    {categoryIcons[meta.id]}
                    <span className='font-medium text-sm'>{meta.label}</span>
                  </div>
                  {getToggle(meta)}
                </div>

                <p className='text-sm text-muted-foreground leading-relaxed'>
                  {meta.description}
                </p>

                <ul className='space-y-1'>
                  {meta.storageUsage.map((usage) => (
                    <li
                      key={usage}
                      className='text-xs text-muted-foreground flex items-center gap-1.5'
                    >
                      <span className='h-1 w-1 rounded-full bg-muted-foreground flex-shrink-0' />
                      {usage}
                    </li>
                  ))}
                </ul>
              </div>
            </React.Fragment>
          ))}
        </div>

        <DialogFooter className='flex-col-reverse sm:flex-row gap-2'>
          <Button variant='outline' onClick={closeSettings}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Preferences</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
